import axios, { type AxiosError } from "axios";
import type { BrowserOptions } from "@sentry/react";
import { normalizeRoute } from "@/services/sentry-capture-policy";

type SentrySpanJson = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];

type ApiHealthOutcome =
  | "success"
  | "server_error"
  | "rate_limited"
  | "expected_client_error"
  | "cancelled"
  | "transport_error"
  | "unknown";

type ApiTransportFailure = "timeout" | "network_error";

const API_PATH_RE = /^\/(?:api|auth)(?:\/|$)/;
const HTTP_METHOD_RE = /^(?:DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT)$/i;
const DESCRIPTION_METHOD_RE = /^(DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT)\s+(.+)$/i;

function stringAttribute(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function numericAttribute(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

export function normalizeApiMethod(method: string | undefined): string {
  return method && HTTP_METHOD_RE.test(method) ? method.toUpperCase() : "UNKNOWN";
}

function requestUrlFromSpan(span: SentrySpanJson): string | undefined {
  const data = span.data;
  const attributeUrl =
    stringAttribute(data["url.full"]) ??
    stringAttribute(data["http.url"]) ??
    stringAttribute(data["http.target"]);
  if (attributeUrl) return attributeUrl;
  return span.description?.match(DESCRIPTION_METHOD_RE)?.[2];
}

function requestMethodFromSpan(span: SentrySpanJson): string {
  const attributeMethod = stringAttribute(span.data?.["http.request.method"]);
  const descriptionMethod = span.description?.match(DESCRIPTION_METHOD_RE)?.[1];
  return normalizeApiMethod(attributeMethod ?? descriptionMethod);
}

/**
 * Convert a browser request URL to a privacy-safe API endpoint dimension.
 * Absolute URLs must match the configured API origin; relative requests must
 * remain under `/api` or `/auth`. Query strings and fragments never reach the
 * existing route normalizer.
 */
export function normalizeApiEndpoint(
  requestUrl: string | undefined,
  apiBaseUrl: string,
): string | null {
  if (!requestUrl) return null;
  const candidate = requestUrl.trim().replace(DESCRIPTION_METHOD_RE, "$2");

  try {
    let parsed: URL;
    if (candidate.startsWith("/")) {
      parsed = new URL(candidate, "https://api.invalid");
    } else {
      parsed = new URL(candidate);
      if (apiBaseUrl) {
        const configuredApi = new URL(apiBaseUrl);
        if (parsed.origin !== configuredApi.origin) return null;
      }
    }
    if (!API_PATH_RE.test(parsed.pathname)) return null;
    return normalizeRoute(parsed.pathname);
  } catch {
    return null;
  }
}

export function classifyApiSpanOutcome(span: SentrySpanJson): ApiHealthOutcome {
  const statusCode =
    numericAttribute(span.data?.["http.response.status_code"]) ??
    numericAttribute(span.data?.["http.status_code"]);

  if (span.status === "cancelled") return "cancelled";
  if (statusCode === 429) return "rate_limited";
  if (statusCode !== undefined && statusCode >= 500) return "server_error";
  if (statusCode !== undefined && statusCode >= 400) return "expected_client_error";
  if (statusCode !== undefined && statusCode >= 200 && statusCode < 400) return "success";
  if (
    statusCode === 0 ||
    span.status === "deadline_exceeded" ||
    span.status === "internal_error" ||
    span.status === "unavailable"
  ) {
    return "transport_error";
  }
  if (span.status === "ok") return "success";
  return "unknown";
}

function isPrivateHttpAttribute(key: string): boolean {
  const normalized = key.toLowerCase();
  return (
    normalized === "url.full" ||
    normalized === "url.query" ||
    normalized === "url.fragment" ||
    normalized === "http.url" ||
    normalized === "http.target" ||
    normalized.includes("request.body") ||
    normalized.includes("request.header") ||
    normalized.includes("response.body") ||
    normalized.includes("response.header")
  );
}

/**
 * Sanitize one automatic Sentry HTTP span before transmission and add the
 * dimensions consumed by the reliability producer. Non-API spans pass through
 * unchanged so third-party browser traffic cannot pollute API health.
 */
export function prepareApiHealthSpan(
  span: SentrySpanJson,
  apiBaseUrl: string,
  automatedBrowser: boolean,
): SentrySpanJson {
  if (span.op !== "http.client") return span;
  const endpoint = normalizeApiEndpoint(requestUrlFromSpan(span), apiBaseUrl);
  if (!endpoint) return span;

  const method = requestMethodFromSpan(span);
  const data = { ...span.data };
  for (const key of Object.keys(data)) {
    if (isPrivateHttpAttribute(key)) delete data[key];
  }
  data["url.path"] = endpoint;
  data["api.endpoint"] = endpoint;
  data["api.method"] = method;
  data["api.outcome"] = classifyApiSpanOutcome(span);
  data["api.automated"] = automatedBrowser;

  return {
    ...span,
    description: `${method} ${endpoint}`,
    data,
  };
}

/** Return the subtype only when Axios proves a no-response transport failure. */
export function classifyAxiosTransportFailure(error: unknown): ApiTransportFailure | null {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) return null;
  const axiosError = error as AxiosError;
  if (axiosError.response) return null;
  if (axiosError.code === "ECONNABORTED" || axiosError.code === "ETIMEDOUT") {
    return "timeout";
  }
  return "network_error";
}
